import { useCallback, useEffect, useMemo, useState } from 'react';
import QRCode from 'react-qr-code';
import type { CheckoutSession } from '../types/checkout';

type CryptoTransfer = NonNullable<CheckoutSession['crypto_transfer']>;
type CopyTarget = 'amount' | 'address';

interface CryptoTransferCheckoutProps {
  transfer: CryptoTransfer;
}

function parseTimestamp(value: string): number | null {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function formatCountdown(totalSeconds: number | null): string {
  if (totalSeconds === null) {
    return '--:--:--';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
}

function CopyIcon() {
  return (
    <svg aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2m-5-7H5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3"
      />
    </svg>
  );
}

export function CryptoTransferCheckout({ transfer }: CryptoTransferCheckoutProps) {
  const expiresAtMs = useMemo(
    () => parseTimestamp(transfer.payment_expires_at),
    [transfer.payment_expires_at],
  );
  const serverTimeMs = useMemo(() => {
    const serverTimeMs = parseTimestamp(transfer.server_time);
    return serverTimeMs;
  }, [transfer.server_time]);

  const getRemainingSeconds = useCallback((): number => {
    const clientRequestStartedAtMs = transfer.client_request_started_at_ms;
    if (
      expiresAtMs === null ||
      serverTimeMs === null ||
      typeof clientRequestStartedAtMs !== 'number' ||
      !Number.isFinite(clientRequestStartedAtMs)
    ) {
      return 0;
    }
    // The server timestamp is conservatively anchored to the local request
    // start, not response/render time. Network and render delay therefore
    // shorten the displayed window instead of extending an expired address.
    const elapsedSinceRequestStart = Math.max(0, Date.now() - clientRequestStartedAtMs);
    const correctedNow = serverTimeMs + elapsedSinceRequestStart;
    return Math.max(0, Math.ceil((expiresAtMs - correctedNow) / 1000));
  }, [expiresAtMs, serverTimeMs, transfer.client_request_started_at_ms]);

  const [remainingSeconds, setRemainingSeconds] = useState<number>(() => getRemainingSeconds());
  const [copiedTarget, setCopiedTarget] = useState<CopyTarget | null>(null);
  const [copyError, setCopyError] = useState(false);

  useEffect(() => {
    const updateCountdown = () => setRemainingSeconds(getRemainingSeconds());
    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timer);
  }, [getRemainingSeconds]);

  const paymentWindowExpired = remainingSeconds === 0;

  const copyValue = useCallback(async (target: CopyTarget, value: string) => {
    if (paymentWindowExpired) {
      return;
    }

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }
      await navigator.clipboard.writeText(value);
      setCopiedTarget(target);
      setCopyError(false);
      window.setTimeout(() => setCopiedTarget(null), 1800);
    } catch {
      setCopiedTarget(null);
      setCopyError(true);
    }
  }, [paymentWindowExpired]);

  const assetNetworkLabel = transfer.asset + ' (' + transfer.network + ')';

  return (
    <>
      <div
        className={
          'mt-7 flex items-center justify-between rounded-2xl px-4 py-3 font-semibold ' +
          (paymentWindowExpired
            ? 'bg-red-50 text-red-700 ring-1 ring-red-100'
            : 'bg-amber-50 text-amber-700 ring-1 ring-amber-100')
        }
        role="status"
      >
        <span className="flex items-center gap-2">
          <span
            className={'h-3 w-3 rounded-full ' + (paymentWindowExpired ? 'bg-red-500' : 'animate-pulse bg-amber-500')}
          />
          {paymentWindowExpired ? 'Payment window expired' : 'Awaiting payment'}
        </span>
        <time className="font-mono text-lg tabular-nums" dateTime={transfer.payment_expires_at}>
          {formatCountdown(remainingSeconds)}
        </time>
      </div>

      <div className="mt-4 overflow-hidden rounded-[28px] bg-white shadow-[0_18px_40px_rgba(79,91,191,0.14)]">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-5">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500 text-lg font-extrabold text-white">
              ₮
            </span>
            <div>
              <p className="text-lg font-extrabold text-slate-900">{transfer.asset}</p>
              <p className="text-sm font-semibold text-slate-500">{transfer.network}</p>
            </div>
          </div>
          <p className="text-right text-lg font-extrabold text-slate-900">
            {transfer.amount} <span className="text-sm text-slate-500">{transfer.asset}</span>
          </p>
        </div>

        <div className="px-5 pb-6 pt-5">
          {paymentWindowExpired ? (
            <div className="rounded-2xl bg-red-50 px-4 py-3 text-center text-sm font-bold leading-5 text-red-700 ring-1 ring-red-100">
              Do not send funds to this address. Keep this page open while we check the final payment status.
            </div>
          ) : (
            <p className="text-center text-sm font-bold leading-6 text-slate-700">
              Only send {transfer.asset} on the {transfer.network} network to this address.
            </p>
          )}

          <div className="mx-auto mt-5 flex h-[216px] w-[216px] items-center justify-center rounded-3xl bg-white p-4 ring-1 ring-slate-200">
            {paymentWindowExpired ? (
              <div
                aria-label="Payment QR code unavailable because the payment window expired"
                className="flex h-full w-full flex-col items-center justify-center rounded-2xl bg-slate-100 text-center text-slate-500"
                role="img"
              >
                <svg aria-hidden="true" className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="mt-2 text-sm font-bold">QR code expired</span>
              </div>
            ) : (
              <div
                aria-label={'QR code for the full ' + assetNetworkLabel + ' receiving address'}
                data-qr-value={transfer.receiving_address}
                data-testid="crypto-address-qr"
                role="img"
              >
                <QRCode
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                  level="M"
                  size={184}
                  value={transfer.receiving_address}
                  viewBox="0 0 256 256"
                />
              </div>
            )}
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <p className="text-sm font-semibold text-slate-500">Amount</p>
              <div className="mt-1 flex items-center gap-3">
                <p className="min-w-0 flex-1 break-all text-lg font-extrabold text-slate-900">
                  {transfer.amount} {transfer.asset}
                </p>
                <button
                  aria-label="Copy amount"
                  className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-35"
                  disabled={paymentWindowExpired}
                  onClick={() => void copyValue('amount', transfer.amount)}
                  type="button"
                >
                  <CopyIcon />
                </button>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-slate-500">Wallet address</p>
              <div className="mt-1 flex items-start gap-3">
                <p className="min-w-0 flex-1 break-all font-mono text-sm font-bold leading-6 text-slate-900">
                  {transfer.receiving_address}
                </p>
                <button
                  aria-label="Copy wallet address"
                  className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-35"
                  disabled={paymentWindowExpired}
                  onClick={() => void copyValue('address', transfer.receiving_address)}
                  type="button"
                >
                  <CopyIcon />
                </button>
              </div>
            </div>
          </div>

          <p aria-live="polite" className="mt-4 min-h-5 text-center text-xs font-semibold text-slate-500">
            {copiedTarget === 'amount' ? 'Amount copied' : null}
            {copiedTarget === 'address' ? 'Wallet address copied' : null}
            {copyError ? 'Copy failed. Please select and copy the value manually.' : null}
          </p>
        </div>
      </div>
    </>
  );
}
